import React, { useEffect, useState } from 'react';
import { Drawer, Upload, Button, message, Progress, Typography, Space, Divider, Select, Checkbox } from 'antd';
import { InboxOutlined, UploadOutlined, DownloadOutlined, FileExcelOutlined } from '@ant-design/icons';
import { organizationAPIWithErrorHandling } from '../../api/organization/organization';
import { neutral, spacing } from '../../theme/tokens';

const { Dragger } = Upload;
const { Text } = Typography;

const UploadModal = ({ 
  group = false,
  visible = false, 
  onCancel, 
  onOk, 
  title = "Upload File",
  acceptedFileTypes = ".pdf,.doc,.docx,.jpg,.jpeg,.png",
  maxFileSize = 10, // MB
  multiple = false,
  // Thêm props cho tải file mẫu
  showSampleDownload = false,
  sampleFileUrl = null,
  sampleFileName = "sample_template.xlsx",
  onDownloadSample = null,
  showAssetCreate = false
}) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingSample, setDownloadingSample] = useState(false);
  const [isAssetCreate, setIsAssetCreate] = useState(false);
  
  const [organizationIds, setOrganizationIds] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const flattenTreeData = (nodes, parentId = 0) => {
    let result = [];
    nodes.forEach((node) => {
      result.push({
        id: node.id,
        pId: parentId,
        value: String(node.id),
        title: node.name,
        label: node.name,
        isLeaf: node.children_recursive?.length === 0,
      });

      if (node.children_recursive && node.children_recursive.length > 0) {
        result = result.concat(
          flattenTreeData(node.children_recursive, node.id)
        );
      }
    });
    return result;
  };
    
  // Load Organizations for select options
  const loadOrganizations = async () => {
    try {
      const response =
        await organizationAPIWithErrorHandling.getTreeStructure();
      if (response.success) {
        const rawData = response.data;
        const flatTree = flattenTreeData(rawData);
        
        setTreeData(flatTree); // Dùng cho TreeSelect
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  useEffect(() => {
    if(!group){
      loadOrganizations();
    }
    
  }, [group]);

  // Xử lý trước khi upload
  const beforeUpload = (file) => {
    const isValidType = checkFileType(file);
    const isValidSize = file.size / 1024 / 1024 < maxFileSize;

    if (!isValidType) {
      message.error(`Chỉ chấp nhận các file: ${acceptedFileTypes}`);
      return false;
    }

    if (!isValidSize) {
      message.error(`File phải nhỏ hơn ${maxFileSize}MB!`);
      return false;
    }

    return false; // Ngăn upload tự động
  };

  // Kiểm tra loại file
  const checkFileType = (file) => {
    const acceptedTypes = acceptedFileTypes.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    return acceptedTypes.includes(fileExtension);
  };

  // Xử lý thay đổi file list
  const handleChange = (info) => {
    let newFileList = [...info.fileList];
    
    // Giới hạn số lượng file nếu không cho phép multiple
    if (!multiple) {
      newFileList = newFileList.slice(-1);
    }

    // Cập nhật trạng thái file
    newFileList = newFileList.map(file => {
      if (file.response) {
        file.url = file.response.url;
      }
      return file;
    });

    setFileList(newFileList);
  };

  // Xóa file
  const handleRemove = (file) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    message.success('Đã xóa file thành công');
  };

  // Xử lý tải file mẫu
  const handleDownloadSample = async () => {
    setDownloadingSample(true);
    
    try {
      if (onDownloadSample) {
        // Gọi callback function để tải file
        await onDownloadSample();
      } else if (sampleFileUrl) {
        // Tải file trực tiếp từ URL
        const link = document.createElement('a');
        link.href = sampleFileUrl;
        link.download = sampleFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        message.error('Không thể tải file mẫu!');
        return;
      }
      
      message.success('Đã tải file mẫu thành công!');
    } catch (error) {
      console.error('Download error:', error);
      message.error('Tải file mẫu thất bại!');
    } finally {
      setDownloadingSample(false);
    }
  };

  // Đơn giản hóa việc xử lý upload - chỉ trả về file gốc
  const processFiles = () => {
    return fileList.map(file => {
      return {
        originFileObj: file.originFileObj || file,
        name: file.name,
        size: file.size,
        type: file.type
      };
    });
  };

  // Xử lý upload - không cần simulate nữa
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Vui lòng chọn file để upload!');
      return;
    }

    if (organizationIds.length === 0 && !group) {
      message.warning('Vui lòng chọn đơn vị!');
      return;
    }

    setUploading(true);
    setUploadProgress(50); // Hiển thị progress

    try {
      const processedFiles = processFiles();
      
      // Callback với file thực tế
      if (onOk) {
        await onOk({
          files: processedFiles,
          organization_ids: organizationIds,
          is_asset_create: isAssetCreate
        });
      }
      
      setUploadProgress(100);
      message.success('Upload thành công!');
      
      // Reset state sau một chút delay để user thấy progress 100%
      setTimeout(() => {
        handleCancel();
      }, 500);
      
    } catch (error) {
      message.error('Upload thất bại!');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Xử lý hủy
  const handleCancel = () => {
    setFileList([]);
    setUploading(false);
    setUploadProgress(0);
    if (onCancel) {
      onCancel();
    }
  };

  const uploadProps = {
    multiple,
    fileList,
    beforeUpload,
    onChange: handleChange,
    onRemove: handleRemove,
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true,
      showDownloadIcon: false,
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <UploadOutlined />
          {title}
        </Space>
      }
      placement="right"
      width="min(640px, 100vw)"
      open={visible}
      onClose={handleCancel}
      destroyOnClose
      styles={{ body: { background: neutral[50], padding: spacing[6] } }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing[2] }}>
          <Button onClick={handleCancel} disabled={uploading}>
            Hủy
          </Button>
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0}
          >
            {uploading ? 'Đang upload...' : 'Upload'}
          </Button>
        </div>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Tải file mẫu */}
        {showSampleDownload && (
          <>
            <div style={{ 
              padding: '16px', 
              background: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Space>
                    <FileExcelOutlined style={{ color: '#128555', fontSize: '18px' }} />
                    <div>
                      <Text strong>Tải file mẫu</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Tải về file mẫu để xem định dạng dữ liệu yêu cầu.<br/>
                        Chấp nhận các loại file: <Text code>{acceptedFileTypes}</Text>.<br/>
                        Kích thước tối đa: <Text strong>{maxFileSize}MB</Text>{multiple && <span> | Cho phép chọn nhiều file</span>}
                      </Text>
                    </div>
                  </Space>
                  <Button 
                    type="primary"
                    ghost
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadSample}
                    loading={downloadingSample}
                    size="small"
                  >
                    {downloadingSample ? 'Đang tải...' : 'Tải mẫu'}
                  </Button>
                </div>
              </Space>
            </div>
            <Divider style={{ margin: '10px 0' }}>Hoặc</Divider>
          </>
        )}

        {!group && 
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            placeholder="Chọn đơn vị"
            options={treeData}
            value={organizationIds}
            onChange={(values) => setOrganizationIds(values)}
          />
        }
        
        {showAssetCreate && (
          <div style={{ marginBottom: '10px' }}>
            <Checkbox 
              checked={isAssetCreate} 
              onChange={(e) => setIsAssetCreate(e.target.checked)}
            >
              Tạo tài sản từ dữ liệu được upload(nếu có)?
            </Checkbox>
          </div>
        )}

        {/* Upload area */}
        <Dragger {...uploadProps} style={{ padding: '14px 0' }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ fontSize: '40px', color: '#0070D7' }} />
          </p>
          <p className="ant-upload-text">
            Kéo thả file vào đây hoặc click để chọn file
          </p>
          <p className="ant-upload-hint">
            {multiple ? 'Hỗ trợ chọn nhiều file cùng lúc' : 'Chỉ chọn được 1 file'}
          </p>
        </Dragger>

        {/* Progress bar khi đang upload */}
        {uploading && (
          <div>
            <Text>Đang upload file...</Text>
            <Progress 
              percent={uploadProgress} 
              status={uploadProgress === 100 ? 'success' : 'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
            />
          </div>
        )}
        
      </Space>
    </Drawer>
  );
};

export default UploadModal;